import { useEffect, useRef, useState } from "react";
import { Message } from "semantic-ui-react";
import { factories, models, Report, service } from "powerbi-client";
import { useRestClientContext } from "../../contexts/RestClientContext";
import { useTeamContext } from "../../contexts/TeamContext";
import { FetchReportResponse } from "../../types/reports";
import Preloader from "../UI/Preloader/Preloader";
import "./PowerBiReportEmbed.scss";

const powerBi = new service.Service(factories.hpmFactory, factories.wpmpFactory, factories.routerFactory);
// Used to find the team filter programmatically (without hard coding PowerBI element IDs).
const TEAM_NAME_COLUMN = "public teams.name";

interface PowerBiReportEmbedProps {
	reportId: number;
}

export default function PowerBiReportEmbed({ reportId }: PowerBiReportEmbedProps) {
	const client = useRestClientContext();
	const team = useTeamContext();
	const reportContainerRef = useRef<HTMLDivElement>(null);
	const [report, setReport] = useState<FetchReportResponse | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		setIsLoading(true);
		setError(null);
		setReport(null);

		client.v3
			.fetchReport(team, reportId)
			.then(setReport)
			.catch((err: Error) => setError(err.message ?? "Failed to load report"))
			.finally(() => setIsLoading(false));
	}, [client, team, reportId]);

	useEffect(() => {
		if (!report || !reportContainerRef.current) return;

		const container = reportContainerRef.current;
		const embedConfig: models.IReportEmbedConfiguration = {
			type: "report",
			id: report.power_bi_report.id,
			embedUrl: report.power_bi_report.embedUrl,
			accessToken: report.embed_token.token,
			tokenType: models.TokenType.Embed,
			pageName: report.report.default_page_id ?? undefined,
			settings: {
				layoutType: models.LayoutType.Custom,
				customLayout: {
					displayOption: models.DisplayOption.FitToWidth,
				},
				panes: {
					filters: {
						expanded: false,
					},
				},
			},
		};

		const embeddedReport = powerBi.embed(container, embedConfig) as Report;

		const clearTeamNameSlicers = async () => {
			const page = await embeddedReport.getActivePage();
			// We need to find the slicer that specifies the team name.
			const visuals = await page.getVisuals();
			const slicers = visuals.filter((visual) => visual.type === "slicer");

			// getSlicerState() returns a promise, so wait for this.
			await Promise.all(
				slicers.map(async (slicer) => {
					const state = await slicer.getSlicerState();
					// Filter out any other slicers. A little inefficient but prevents it being flaky in different
					// environments.
					const isTeamNameSlicer = state.filters.some(
						(filter) => "column" in filter.target && filter.target.column === TEAM_NAME_COLUMN
					);
					if (!isTeamNameSlicer) return;

					// Defaults to 'any'.
					await slicer.setSlicerState({ filters: [] });
				})
			);
		};

		embeddedReport.on("loaded", clearTeamNameSlicers);

		return () => {
			embeddedReport.off("loaded");
			powerBi.reset(container);
		};
	}, [report]);

	return isLoading ? (
		<Preloader />
	) : error ? (
		<Message error>{error}</Message>
	) : (
		<div ref={reportContainerRef} className="powerbi-report-embed" />
	);
}
